const AUTH_DEBUG_PARAM = 'auth_debug';
const AUTH_DEBUG_STORAGE_KEY = 'mcv.authDebug';

type AuthDebugDetails = Record<string, unknown>;

const getStoredDebugPreference = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (typeof window.localStorage?.getItem !== 'function') return false;

  try {
    return window.localStorage.getItem(AUTH_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const setStoredDebugPreference = (enabled: boolean) => {
  if (typeof window === 'undefined') return;

  try {
    if (enabled && typeof window.localStorage?.setItem === 'function') {
      window.localStorage.setItem(AUTH_DEBUG_STORAGE_KEY, '1');
    } else if (!enabled && typeof window.localStorage?.removeItem === 'function') {
      window.localStorage.removeItem(AUTH_DEBUG_STORAGE_KEY);
    }
  } catch {
    // Debug logging must never affect auth behavior.
  }
};

const readDebugPreference = (): boolean => {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  const queryValue = params.get(AUTH_DEBUG_PARAM);

  if (queryValue === '1' || queryValue === 'true') {
    setStoredDebugPreference(true);
    return true;
  }

  if (queryValue === '0' || queryValue === 'false') {
    setStoredDebugPreference(false);
    return false;
  }

  return import.meta.env.VITE_AUTH_DEBUG === 'true' || getStoredDebugPreference();
};

const summarizeSearch = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const summary: Record<string, string> = {};

  params.forEach((value, key) => {
    if (key.startsWith('__clerk')) {
      summary[key] = value ? '[redacted]' : '';
      return;
    }

    summary[key] = value;
  });

  return summary;
};

export const summarizeUrl = (raw: string | null | undefined): unknown => {
  if (!raw) return raw ?? null;

  try {
    const url = new URL(raw, window.location.origin);
    return {
      origin: url.origin,
      pathname: url.pathname,
      search: summarizeSearch(url.search),
      hash: url.hash ? '[present]' : '',
    };
  } catch {
    return raw;
  }
};

const currentLocationSummary = () => {
  if (typeof window === 'undefined') return null;

  return {
    host: window.location.host,
    pathname: window.location.pathname,
    search: summarizeSearch(window.location.search),
    hash: window.location.hash ? '[present]' : '',
  };
};

export const authDebug = (event: string, details: AuthDebugDetails = {}) => {
  if (!readDebugPreference()) return;

  console.info('[auth-flow]', event, {
    ...details,
    location: currentLocationSummary(),
  });
};
